/* The wire contract, mirroring app/api/schemas.py.
 *
 * Hand-written rather than generated. The API has fifteen response shapes, and
 * a generator would buy less than it costs in build machinery — but that means
 * these types are a CLAIM about the server, not a proof. When one drifts, the
 * API's own tests are what catch it; TypeScript only catches misuse on this
 * side.
 *
 * Times are SECONDS. Never frames, never timecode strings. The one timecode in
 * this system is burned into the video's pixels and is the model's problem. */

export type Hands = "left" | "right" | "both" | "idle";
export type SubtaskStatus = "PENDING" | "APPROVED" | "REJECTED";
export type Stage = "AI" | "ANNOTATOR" | "QA" | "DELIVERED";
export type JobStatus =
  | "PENDING" | "IN_PROGRESS" | "COMPLETED"
  | "APPROVED" | "REJECTED" | "FAILED";
export type Decision =
  | "AUTO_ACCEPT" | "RECHECK" | "HUMAN_REVIEW" | "AUDIT" | "REJECT";

export interface Verdict {
  trust_score: number;
  decision: Decision;
  reason_codes: string[];
  attribute_scores: Record<string, number>;
}

/** One thing the judge checked. `verdict` here speaks about the ANNOTATION:
 *  a field the two models agreed on supports it, one they differed on
 *  contradicts it. Fetched per sub-task, not carried on the job document —
 *  ~9 rows each would take a 77-sub-task job from 59 KB to over 130 KB. */
export interface Evidence {
  check_name: string;
  kind: string;
  attribute: string;
  verdict: "supports" | "contradicts" | "insufficient" | "not_applicable";
  severity: string;
  score: number | null;
  reason_code: string | null;
  detail: string | null;
  model_id: string | null;
  is_independent: boolean;
}

export interface SubtaskEvidence {
  subtask_id: number;
  /** Null when nothing judged this sub-task — which is not the same as clean. */
  verdict: Verdict | null;
  evidence: Evidence[];
}

export interface Subtask {
  id: number;
  subtask_index: number;
  custom_title: string | null;
  description: string;
  verb: string | null;
  object_name: string | null;
  tool: string | null;
  target: string | null;
  hands: Hands;
  start_seconds: number;
  end_seconds: number;
  length_seconds: number;
  /** What the model says it SAW. Ours, not the customer's — shown to the
   *  reviewer so a flagged event can be diagnosed. */
  reason: string | null;
  ai_confidence: number | null;
  status: SubtaskStatus;
  rejection_reason: string | null;
  ai_check_valid: boolean;
  edited: boolean;
  verdict: Verdict | null;
  /** READ-ONLY for the crowd: LLM-2 the judge agreed with LLM-1 on every field,
   *  so the sub-task goes to the client as it is. The server refuses edits
   *  (HTTP 423); the workspace shows it read-only. */
  locked?: boolean;
}

export interface Task {
  id: number;
  task_index: number;
  title: string;
  description: string | null;
  start_seconds: number;
  end_seconds: number;
  subtasks: Subtask[];
}

export interface Video {
  id: number;
  s3_path: string;
  duration_seconds: number | null;
  fps: number | null;
  width: number | null;
  height: number | null;
  session_code: string | null;
  category: string | null;
  sub_category: string | null;
}

export interface Guideline {
  id: number;
  code: string;
  name: string;
  taxonomy_json: {
    verbs?: string[];
    objects?: string[];
    tools?: string[];
    targets?: string[];
    hands?: string[];
  };
  description_rules: {
    tense?: string;
    min_words?: number;
    must_name_hands?: boolean;
    blacklist?: string[];
    manual_checks?: string[];
    /** Both read by `api/checks.py`. They were absent here, so `rulesFrom`
     *  could only hardcode them and the two halves of the pair drifted. */
    accepted_hand_mentions?: string[];
    min_subtask_seconds?: number;
  };
  rate_per_minute: number;
  currency: string;
}

export interface JobEvent {
  id: number;
  stage: Stage;
  status: JobStatus;
  user_id: number | null;
  user_name: string | null;
  message: string | null;
  created_at: string;
}

export interface JobSummary {
  job_id: number;
  video_path: string;
  duration_seconds: number | null;
  category: string | null;
  sub_category: string | null;
  session_code: string | null;
  current_stage: Stage;
  current_status: JobStatus;
  held_by: number | null;
  held_by_name: string | null;
  task_count: number;
  subtask_count: number;
  approved_count: number;
  flagged_count: number;
}

/** A row of the queue: one VIDEO from the corpus.
 *
 *  `job_id`, `current_stage` and `current_status` are null for footage nothing
 *  has started on. That is a real state — "we have this video and have not
 *  processed it" — and the UI must render it as such rather than inventing a
 *  stage. Everything downstream branches on `job_id === null`. */
export interface VideoRow {
  video_id: number;
  video_path: string;
  /** Presigned URL for the video itself. Still the thumbnail fallback when
   *  `poster_url` is absent or fails to load. */
  media_url: string | null;
  /** Presigned URL for a stored ~20 KB JPEG of the frame one second in, when
   *  one has been generated (tools/make_posters.py). Null on a deployment
   *  whose posters have not been built, and a 404 for an individual video that
   *  was added since — both handled the same way, by falling back. */
  poster_url: string | null;
  duration_seconds: number | null;
  fps: number | null;
  width: number | null;
  height: number | null;
  category: string | null;
  sub_category: string | null;
  session_code: string | null;

  job_id: number | null;
  current_stage: Stage | null;
  current_status: JobStatus | null;
  held_by: number | null;
  held_by_name: string | null;

  task_count: number;
  subtask_count: number;
  approved_count: number;
  /** Rejected counts as reviewed — see v_video_queue. */
  rejected_count: number;
  flagged_count: number;
}

export interface JobDetail {
  /** 6.3's by-hand confirmations, in the guideline's order. Empty until
   *  someone ticks one. */
  manual_checks: boolean[];
  job_id: number;
  current_stage: Stage;
  current_status: JobStatus;
  video: Video;
  guideline: Guideline;
  ai_run_id: number | null;
  prompt_version: string | null;
  model_id: string | null;
  media_url: string | null;
  /** 26 Oct 2026: "360p" when media_url is the light playback copy, else
   *  "original"; the master to fall back to if the copy fails (null when
   *  media_url is the master); the poster shown until the first frame. */
  media_variant?: "360p" | "original";
  media_fallback_url?: string | null;
  poster_url?: string | null;
  tasks: Task[];
  events: JobEvent[];
  qa_message: string | null;
}

export interface CheckIssue {
  subtask_id: number | null;
  field: string;
  code: string;
  severity: "error" | "warning";
  message: string;
}

export interface CheckReport {
  ok: boolean;
  issues: CheckIssue[];
}

/** A partial edit to one sub-task. Named here rather than inferred at each
 *  call site so the inspector, the autosave queue and the client all speak
 *  about the same object — an inferred `Record<string, unknown>` in the middle
 *  would silently accept a misspelt field name. */
export interface SubtaskPatch {
  custom_title?: string | null;
  description?: string;
  verb?: string | null;
  object_name?: string | null;
  tool?: string | null;
  target?: string | null;
  hands?: Hands;
  start_seconds?: number;
  end_seconds?: number;
}

/** The reasons the annotator picks from, worded as
 *  Video_Annotation_Tool_v1.6 words them — each says what is wrong AND how you
 *  would recognise it, which is what makes the picker answerable at a glance.
 *  The bare tags ("wrong boundaries") were shorter and told the reader nothing
 *  they did not already know.
 *
 *  The fifth is ours: task boundaries are fixed in v1 (8.1.4), so a bad task
 *  split has nowhere else to be reported. */
export const REJECTION_REASONS = [
  "Wrong boundaries — start/end don't match the action",
  "Wrong label — verb or object is incorrect",
  "Action not visible — hands leave the frame",
  "Unusable footage — blur / glare / obstruction",
  "Task segmentation wrong — this belongs to a different task",
] as const;

/* ---------------------------------------------------------- accuracy (008) */

export interface AccuracyTotals {
  videos: number;
  subtasks: number;
  approved: number;
  /** Approved sub-tasks that have a ground-truth event — the (a) denominator. */
  approved_scored: number;
  /** Approved sub-tasks with no GT event (the crowd merged them) — not scored. */
  approved_no_gt: number;
  no_gt: number;
  wrong: number;
  /** (a) = 1 − wrong approved / approved, as a percentage. Null with nothing approved. */
  a_pct: number | null;
  /** (b) = not approved / all, as a percentage. */
  b_pct: number | null;
}

export interface VideoAccuracy extends AccuracyTotals {
  video_id: number;
  video: string;
  crowd_added: number;
  excluded: boolean;
  exclusion_reason: string | null;
}

export interface AccuracyReport {
  gt_version: string | null;
  eval_name: string | null;
  gt_versions: string[];
  eval_names: string[];
  /** The headline: every video except the excluded ones. */
  included: AccuracyTotals | null;
  all: AccuracyTotals | null;
  excluded_count: number;
  unscored_decisions: number;
  subtasks_corrected: number;
  videos: VideoAccuracy[];
}

export type GtMatch = "YES" | "NO" | "AMBIGUOUS" | "NO_GT";

export interface SubtaskMatch {
  subtask_id: number;
  decision: string | null;
  match: GtMatch;
}

/** One video's Accuracy-page row and each sub-task's GT match (video screen). */
export interface JobAccuracy {
  gt_version: string | null;
  eval_name: string | null;
  video: VideoAccuracy | null;
  subtasks: SubtaskMatch[];
}

/** Object and hand boxes for the player overlay (api highlights.py, 31 Oct
 *  2026). A display aid only. Times in seconds of the video, boxes as fractions
 *  (0-1) of the frame: [t, x1, y1, x2, y2]. */
export type HighlightRole = "hand" | "object" | "tool" | "target";
export interface HighlightTrack {
  name: string;
  role: HighlightRole;
  points: [number, number, number, number, number][];
}
export interface HighlightSegment {
  start: number;
  end: number;
  object_name: string | null;
  tool: string | null;
  target: string | null;
  tracks: HighlightTrack[];
}
export interface Highlights {
  available: boolean;
  detector?: { name?: string; fps?: number };
  generated_at?: string | null;
  segments?: HighlightSegment[];
}

/** Tool-wide switches: one value for every user and every video (GET /api/settings). */
export interface AppSettings {
  highlights_enabled: boolean;
}
